import { buildConnection } from '$app/arrp-hub-connection-builder'
import apiConnection from '$app/plugins/api-connection'
import defaultPagingInfo from '$app/default-paging-info'

const connection = buildConnection('Enrollment');

// STATE
const state = {
  loading: true,
  nextId: 0,
  pagingInfo: defaultPagingInfo(),
  items: [],
  locks: [],
  totalItems: 0,
  searchModel: {
    fleId: '',
    lastname: '',
    firstName: '',
    pcavId: null,
    matchedOn: null
  },
  itemsToLinkToProvider: []
}

// MUTATIONS
const mutations = {
  updateSearchModel(state, searchModel) {
    state.searchModel = searchModel;
    state.pagingInfo.page = 1;
  },
  updateTotalItems(state, totalItems) {
    state.totalItems = totalItems;
  },
  addItem(state, item) {
    state.items.push(item);
    state.nextId = item.id + 1;
  },
  updateItem(state, item) {
    const idx = state.items.findIndex(x => x.id === item.id);
    if (idx !== -1) {
      state.items.splice(idx, 1, { ...item });
    }
  },
  deleteItem(state, item) {
    const idx = state.items.findIndex(x => x.id === item.id);
    if (idx !== -1) {
      state.items.splice(idx, 1);
    }
  },
  updateItems(state, items) {
    state.items = [...items];
  },
  updatePagingInfo(state, pagingInfo) {
    state.pagingInfo = { ...pagingInfo };
  },
  setLoading(state, loading) {
    state.loading = loading;
  },
  setLocks(state, locks) {
    state.locks = locks;
  }
}

// ACTIONS
const actions = ({
  async connect({ commit, dispatch }) {
    connection.onLocksUpdated((locks) => commit('setLocks', locks));
    connection.onEntitiesUpdated(() => dispatch('search'));
    await connection.connect();
  },
  async disconnect() {
    await connection.disconnect();
  },
  lockItem({ commit }, item) {
    connection.lockById(item.pcamId);
  },
  unlockItem({ commit }, item) {
    connection.unlockById(item.pcamId);
  },
  search({ commit, state }) {
    const searchModel = { ...state.searchModel, pagingInfo: state.pagingInfo };
    commit('setLoading', true);
    apiConnection.post('api/arrp/SearchEnrollments', searchModel).then(resp => {
      const items = resp.data.items.map(p => ({ ...p, errors: p.rowErrors }));

      commit('updateItems', items);
      commit('updateTotalItems', resp.data.totalItems);
    }).finally(() => commit('setLoading', false));
  },
  unmatch({ dispatch }, item) {
    return new Promise((resolve, reject) => {
      const { pcamId } = item;
      apiConnection.post(`api/arrp/unmatchEnrollment`, null, { params: { pcamId } })
        .then(resp => {
          if (resp && resp.data && resp.data.success) {
            dispatch('search');
            resolve(resp.data);
          } else {
            reject(new Error('Unable To Unmatch Enrollment'))
          }
        })
        .catch(_ => {
          reject(new Error('Unable To Unmatch Enrollment'));
        });
    });
  },
  itemAdded({ dispatch }, item) {
    apiConnection.post('api/arrp/saveEnrollment', { ...item })
      .catch(() => this._vm.$snackbar('Unable to Create Enrollment', 'error'));
  },
  itemEdited({ dispatch }, item) {
    apiConnection.post('api/arrp/saveEnrollment', { ...item })
      .catch(() => this._vm.$snackbar('Unable to Save Enrollment', 'error'));
  },
  deleteItem({ dispatch }, item) {
    return new Promise((resolve, reject) => {
      const { pcamId } = item;
      apiConnection.delete(`api/arrp/deleteEnrollment`, { params: { pcamId } })
        .then(resp => {
          if (resp && resp.data && resp.data.success) {
            resolve(resp.data);
          } else {
            reject(new Error('Unable To Delete Enrollment'))
          }
        })
        .catch(_ => {
          reject(new Error('Unable To Delete Enrollment'));
        });
    });
  },
  sortItems({ commit, state, dispatch }, sortValues) {
    const pagingInfo = { ...state.pagingInfo, page: 1, ...sortValues };
    commit('updatePagingInfo', pagingInfo);
    dispatch('search');
  },
  pagingInfoUpdated({ commit }, pagingInfo) {
    commit('updatePagingInfo', pagingInfo);
  },
  getPagedData({ commit, state, dispatch }, page) {
    const pagingInfo = { ...state.pagingInfo, page };
    commit('updatePagingInfo', pagingInfo);
    dispatch('search');
  }
})

// GETTERS
const getters = {
  searchModel: state => state.searchModel,
  currentPage: state => state.items.map(item => ({ ...item, locked: state.locks.includes(item.pcamId) }))
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
